/**
 * Hook for managing social connections
 */

import { useState, useEffect, useCallback } from 'react';
import socialConnectionsApi from '@/services/social-connections-api';

interface Connection {
  id: string;
  platform_type: string;
  platform_name: string;
  platform_user_id: string;
  platform_user_name: string;
  icon_url?: string;
  created_at?: string;
  updated_at?: string;
}

interface UseSocialConnectionsReturn {
  connections: Connection[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  initiateOAuth: (platform: string) => Promise<{ authorization_url: string }>;
  disconnect: (connectionId: string, optimistic?: boolean) => Promise<void>;
  updateSettings: (connectionId: string, settings: Record<string, unknown>) => Promise<void>;
  logInterest: (platform: string) => Promise<void>;
}

export function useSocialConnections(): UseSocialConnectionsReturn {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await socialConnectionsApi.listConnections();
      setConnections(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch connections';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const initiateOAuth = useCallback(async (platform: string) => {
    try {
      setError(null);
      return await socialConnectionsApi.initiateOAuth(platform);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to initiate OAuth';
      setError(message);
      throw err;
    }
  }, []);

  const disconnect = useCallback(async (connectionId: string, optimistic = false) => {
    setError(null);
    const originalConnections = connections;

    if (optimistic) {
      setConnections(prev => prev.filter(c => c.id !== connectionId));
    }

    try {
      await socialConnectionsApi.disconnectAccount(connectionId);
      if (!optimistic) {
        setConnections(prev => prev.filter(c => c.id !== connectionId));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to disconnect account';
      setError(message);
      if (optimistic) {
        setConnections(originalConnections);
      }
      throw err;
    }
  }, [connections]);

  const updateSettings = useCallback(
    async (connectionId: string, settings: Record<string, unknown>) => {
      try {
        setError(null);
        const response = await socialConnectionsApi.updateConnectionSettings(
          connectionId,
          settings
        );
        // Update local state
        setConnections(prev =>
          prev.map(c =>
            c.id === connectionId ? { ...c, ...response.connection } : c
          )
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update settings';
        setError(message);
        throw err;
      }
    },
    []
  );

  const logInterest = useCallback(async (platform: string) => {
    try {
      setError(null);
      await socialConnectionsApi.logInterest(platform);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to log interest';
      setError(message);
      throw err;
    }
  }, []);

  return {
    connections,
    loading,
    error,
    refetch: fetchConnections,
    initiateOAuth,
    disconnect,
    updateSettings,
    logInterest,
  };
}

export default useSocialConnections;
